import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";
import {
  SbuxGenericGrid,
  SbuxGridToolbar,
} from "../../../../../components/SbuxDataGrid/SbuxGenericGrid";
import {
  Box,
  Paper,
  Button,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SbuxDialog2 from "../../../../../components/SbuxDialog2";
import { getTimezoneSelector } from "../../../../../selectors/settingsSelector";
import { createOrUpdateMainHours } from "../../../../../services/mainHours";
import { isEmptyString } from "../../../../../utils/stringUtil";
import { FLEX_COL_FILL } from "../../../../../themes/sbux/components";
import { dataNormalizer } from "./util";
import { mainStyles } from "./styles";
import useCss from "../../../../../hooks/useCss";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import { masterColumns } from "./columns";
import MainHoursAdd from "./MainHoursAdd";
import MainHoursEdit from "./MainHoursEdit";
import type { ReactNode } from "react";
import { useGridApiRef, type GridColDef } from "@mui/x-data-grid";

interface Column {
  headerName?: string;
  [key: string]: unknown;
}

interface RowData {
  hoursCode?: string | number;
  hoursName?: string;
  description?: string;
  showForManagers?: boolean;
  readOnly?: boolean;
  hoursConfig?: Record<string, unknown>[];
  [key: string]: unknown;
}

interface Dataset {
  hoursName?: string;
  hoursCode?: string | number;
  description?: string;
  hoursConfig?: Record<string, unknown>[];
  showForManagers?: boolean;
  readOnly?: boolean;
  [key: string]: unknown;
}

interface Props {
  mainHoursData: Record<string, unknown>[];
  handleRefrehClick: () => Promise<void>;
  isManager: boolean;
  disableAddNew: boolean;
  isLoading?: boolean;
}

const MainHoursMasterTable = ({
  mainHoursData,
  handleRefrehClick,
  isManager,
  disableAddNew,
  isLoading = false,
}: Props) => {
  const classes = useCss(mainStyles);
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(false);
  const [rowToEdit, setRowToEdit] = useState<RowData | undefined>(undefined);
  const [dataset, setDataset] = useState<Dataset>({});
  const apiRef = useGridApiRef();

  const timezone = useAppSelector(getTimezoneSelector);

  const toggleDialogOpen = () => {
    setOpen((p) => !p);
    setEditRow(false);
  };

  const handleOnRowClick = ({ row }: { row: RowData }) => {
    setRowToEdit(row);
    setEditRow(true);
    setOpen(true);
  };

  const handleMemoDataset = (data: Record<string, unknown>) => {
    const newDataset = {
      ...dataset,
      ...(data as Partial<Dataset>),
    };
    setDataset(newDataset);
  };

  const dialogConfig = {
    title: <SbuxTranslate>{"Details"}</SbuxTranslate>,
  };

  const getTranslatedColumns = (columns: Column[]): GridColDef[] =>
    columns.map((col) => {
      const { headerName, ...rest } = col as unknown as GridColDef & {
        headerName?: string;
      };
      return {
        ...rest,
        headerName: headerName ?? "",
        renderHeader: headerName
          ? () => (
              <Paper
                component="span"
                elevation={0}
                className="MuiDataGrid-columnHeaderTitle"
                sx={{ backgroundColor: "transparent" }}
              >
                <SbuxTranslate>{headerName}</SbuxTranslate>
              </Paper>
            )
          : undefined,
      };
    });

  const handleSave = async () => {
    if (!!dataset) {
      const mainHoursItem = { ...dataset };

      if (isEmptyString(mainHoursItem.hoursName ?? "")) {
        return;
      }

      let hoursConfigWithoutIds = [...(mainHoursItem.hoursConfig ?? [])];
      hoursConfigWithoutIds = hoursConfigWithoutIds.reduce<
        Record<string, unknown>[]
      >((acc, cur) => {
        acc.push({
          start: (cur as Record<string, unknown>).start,
          end: (cur as Record<string, unknown>).end,
          description: (cur as Record<string, unknown>).description,
          day: (cur as Record<string, unknown>).day,
          isOpenFor24Hours: (cur as Record<string, unknown>).isOpenFor24Hours,
        });
        return acc;
      }, []);
      const readOnlyValue = isManager
        ? mainHoursItem.readOnly
        : mainHoursItem.readOnly || false;

      const data = {
        ...(editRow && { hoursCode: mainHoursItem.hoursCode }),
        hoursName: mainHoursItem.hoursName,
        description: mainHoursItem.description,
        hoursConfig: [...hoursConfigWithoutIds],
        showForManagers: isManager || mainHoursItem.showForManagers || false,
        readOnly: readOnlyValue,
      };
      toggleDialogOpen();

      await dispatch(createOrUpdateMainHours(data));
    }
  };

  const isFormValid = () => {
    if (!!dataset) {
      const { hoursName } = dataset;
      if (isManager && dataset.readOnly) {
        return true;
      }
      if (isEmptyString(hoursName ?? "")) {
        return true;
      }
      const hoursConfig = (dataset.hoursConfig ?? []) as Array<{
        start: string;
        end: string;
        isOpenFor24Hours: boolean;
      }>;
      const hasInvalidTimeRange = hoursConfig.some((config) => {
        if (config.isOpenFor24Hours) return false;
        if (!config.start || !config.end) return false;
        if (
          !/^\d{2}:\d{2}$/.test(config.start) ||
          !/^\d{2}:\d{2}$/.test(config.end)
        )
          return false;
        if (config.end === "00:00") return false; // midnight end treated as next day
        const [startH, startM] = config.start.split(":").map(Number);
        const [endH, endM] = config.end.split(":").map(Number);
        return endH * 60 + endM < startH * 60 + startM;
      });
      return hasInvalidTimeRange;
    }
    return false;
  };

  const DataGridToolbar = (props: { searchPlaceholder?: ReactNode }) => {
    return (
      <SbuxGridToolbar
        apiRef={apiRef}
        searchPlaceholder={props.searchPlaceholder}
      >
        <Button
          variant="contained"
          size="medium"
          onClick={toggleDialogOpen}
          disabled={disableAddNew}
        >
          {<SbuxTranslate>{"Add New hours"}</SbuxTranslate>}
        </Button>
      </SbuxGridToolbar>
    );
  };

  return (
    <>
      <Box sx={FLEX_COL_FILL}>
        <SbuxGenericGrid
          apiRef={apiRef}
          rows={dataNormalizer(mainHoursData, timezone ?? "")}
          columns={getTranslatedColumns(masterColumns)}
          labelRowsPerPage={<SbuxTranslate>{"Rows per page"}</SbuxTranslate>}
          labelOf={<SbuxTranslate>{"of"}</SbuxTranslate>}
          searchPlaceholder={<SbuxTranslate>{"Search..."}</SbuxTranslate>}
          isLoading={isLoading}
          slots={{ toolbar: DataGridToolbar }}
          slotClasses={{
            toolbar: {
              container: classes.toolbarContainer,
            },
            datagrid: {
              container: classes.datagridContainer,
            },
          }}
          onRowClick={handleOnRowClick}
          clickableRow
        />
      </Box>

      {open && (
        <SbuxDialog2
          open={open}
          config={dialogConfig}
          handleOnClose={toggleDialogOpen}
        >
          <DialogContent dividers>
            {!editRow ? (
              <MainHoursAdd
                memoDataset={handleMemoDataset}
                isManager={isManager}
              />
            ) : (
              rowToEdit && (
                <MainHoursEdit
                  row={rowToEdit}
                  memoDataset={handleMemoDataset}
                  isManager={isManager}
                />
              )
            )}
          </DialogContent>

          <DialogActions>
            <Box className="actionContainer">
              <Button
                variant="contained"
                size="medium"
                disabled={isFormValid()}
                onClick={handleSave}
              >
                <SbuxTranslate>{"Save"}</SbuxTranslate>
              </Button>

              <Button
                variant="outlined"
                size="medium"
                onClick={toggleDialogOpen}
              >
                <SbuxTranslate>{"Close"}</SbuxTranslate>
              </Button>
            </Box>
          </DialogActions>
        </SbuxDialog2>
      )}
    </>
  );
};

export default MainHoursMasterTable;
